/**
 * Color Swatches - Applies swatch colors from a JSON data file
 * 
 * This script loads swatch definitions from swatch-data.json and applies
 * the colors/images to swatch elements on the page.
 * 
 * To update swatches:
 * 1. Add/edit colors in Shopify Admin > Settings > Custom data > Metaobjects > swatch_color
 * 2. Run: node scripts/export-swatches-to-json.js
 * 3. Deploy the updated theme
 */

(function() {
  'use strict';

  let swatchData = null;
  let swatchDataPromise = null;

  /**
   * Load swatch data from JSON file (cached after first load)
   */
  function loadSwatchData() {
    if (swatchDataPromise) return swatchDataPromise;

    // Get the asset URL base from Shopify
    const assetUrl = window.Shopify?.routes?.root 
      ? window.Shopify.routes.root + 'cdn/shop/t/1/assets/swatch-data.json'
      : '/assets/swatch-data.json';
    
    // Try multiple possible paths
    const possibleUrls = [
      document.querySelector('script[src*="color-swatches.js"]')?.src?.replace('color-swatches.js', 'swatch-data.json'),
      '/cdn/shop/files/swatch-data.json',
      '/assets/swatch-data.json'
    ].filter(Boolean);

    swatchDataPromise = tryLoadFromUrls(possibleUrls)
      .then(data => {
        swatchData = data;
        return data;
      })
      .catch(error => {
        console.warn('Color swatches: Could not load swatch data', error);
        swatchData = {};
        return {};
      });

    return swatchDataPromise;
  }

  /**
   * Try loading from multiple URLs until one succeeds
   */
  async function tryLoadFromUrls(urls) {
    for (const url of urls) {
      try {
        const response = await fetch(url);
        if (response.ok) {
          return await response.json();
        }
      } catch (e) {
        // Try next URL
      }
    }
    throw new Error('Could not load swatch data from any URL');
  }

  /**
   * Apply swatch color/image to a swatch element
   */
  function applySwatchStyle(element, colorName) {
    if (!swatchData || !colorName) return;

    const key = colorName.toLowerCase().trim();
    const swatch = swatchData[key];

    if (!swatch) {
      // console.warn(`Color swatch not found: ${colorName}`);
      return;
    }

    // Find the swatch span inside the label
    const swatchSpan = element.querySelector('.swatch') || element;

    if (swatch.image) {
      // Use image if available
      const imageUrl = swatch.image.includes('?') 
        ? swatch.image + '&width=100' 
        : swatch.image + '?width=100';
      swatchSpan.style.setProperty('--swatch--background', `url(${imageUrl})`);
      swatchSpan.classList.remove('swatch--unavailable');
    } else if (swatch.color) {
      // Use color
      swatchSpan.style.setProperty('--swatch--background', swatch.color);
      swatchSpan.classList.remove('swatch--unavailable');
    }
  }

  /**
   * Initialize swatches on the page
   */
  function initializeSwatches() {
    // Find all swatch elements that need color application
    const swatchLabels = document.querySelectorAll('[data-swatch-color]');
    
    if (swatchLabels.length === 0) return;

    loadSwatchData().then(() => {
      swatchLabels.forEach(label => {
        const colorName = label.dataset.swatchColor;
        applySwatchStyle(label, colorName);
      });
    });
  }

  /**
   * Observe for dynamically added swatches (e.g., quick view, AJAX)
   */
  function observeNewSwatches() {
    const observer = new MutationObserver((mutations) => {
      let hasNewSwatches = false;
      
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            if (node.hasAttribute?.('data-swatch-color') || node.querySelector?.('[data-swatch-color]')) {
              hasNewSwatches = true;
            }
          }
        });
      });

      if (hasNewSwatches) {
        initializeSwatches();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initializeSwatches();
      observeNewSwatches();
    });
  } else {
    initializeSwatches();
    observeNewSwatches();
  }

  // Export for manual use if needed
  window.ColorSwatches = {
    load: loadSwatchData,
    apply: applySwatchStyle,
    refresh: initializeSwatches
  };
})();
